import {
  isValidRectangle,
  rectanglesOverlap,
} from 'features/documentMarkup/editor/markupGeometry'
import type {
  MeasuredImage,
  PixelRectanglePolicy,
} from 'features/libra/drawing/pixelRectangleValidation'
import {
  pixelImageRectangle,
  pixelRectangleFromBounds,
  pixelRectangleMeetsMinimumSize,
  validateMeasuredPixelRectangle,
} from 'features/libra/drawing/pixelRectangleValidation'

import type {
  ZoneBoxTarget,
  ZoneParams,
  ZoneRectangle,
} from './zoneAnalysisApi'

type ZoneGeometryRecord = Pick<
  ZoneParams,
  'id' | 'title' | 'zone_reference_image_id' | 'drawing_bounds'
>

type ZonePixelObstacle = PixelRectanglePolicy['obstacles'][number] & {
  target: ZoneBoxTarget
  title: string
}

export interface ZonePixelPolicy extends PixelRectanglePolicy {
  readonly obstacles: readonly ZonePixelObstacle[]
}

const sameRectangle = (first: ZoneRectangle, second: ZoneRectangle) =>
  first.x_start === second.x_start &&
  first.y_start === second.y_start &&
  first.x_end === second.x_end &&
  first.y_end === second.y_end

/** Builds the current same-image geometry policy in natural image pixels. */
export const buildZonePixelPolicy = ({
  documentId,
  zones,
  measuredImage,
  excludeTarget,
}: {
  documentId: string
  zones: readonly ZoneGeometryRecord[]
  measuredImage?: MeasuredImage
  excludeTarget?: ZoneBoxTarget
}): ZonePixelPolicy | { error: string } => {
  if (!documentId) return { error: 'A reference image is required.' }

  const obstacles: ZonePixelObstacle[] = []
  for (const zone of zones) {
    if (zone.zone_reference_image_id !== documentId) continue
    for (const [boxIndex, bounds] of (zone.drawing_bounds ?? []).entries()) {
      if (
        zone.id === excludeTarget?.zoneId &&
        boxIndex === excludeTarget.boxIndex
      )
        continue
      const rectangle = pixelRectangleFromBounds(bounds)
      if (!isValidRectangle(rectangle))
        return {
          error: `${zone.title} has an invalid rectangle ${boxIndex + 1}. Redraw it before adding another rectangle.`,
        }
      obstacles.push({
        id: JSON.stringify([zone.id, boxIndex]),
        target: { zoneId: zone.id, boxIndex },
        title: zone.title,
        rectangle,
      })
    }
  }

  return {
    documentId,
    boundary:
      measuredImage?.documentId === documentId
        ? pixelImageRectangle(measuredImage)
        : undefined,
    obstacles,
  }
}

/** Checks only boxes not matched one-for-one to the saved same-image baseline. */
export const validateZoneGeometryChanges = ({
  baselineZones,
  proposedZones,
  measuredImage,
  forceValidateIds = [],
}: {
  baselineZones: readonly ZoneParams[]
  proposedZones: readonly ZoneParams[]
  measuredImage?: MeasuredImage
  forceValidateIds?: readonly string[]
}): string | undefined => {
  const baselineById = new Map(baselineZones.map((zone) => [zone.id, zone]))
  const forcedIds = new Set(forceValidateIds)

  for (const zone of proposedZones) {
    const previous = baselineById.get(zone.id)
    const forceAll =
      forcedIds.has(zone.id) ||
      !previous ||
      previous.zone_reference_image_id !== zone.zone_reference_image_id
    const baseline =
      previous &&
      previous.zone_reference_image_id === zone.zone_reference_image_id
        ? (previous.drawing_bounds ?? [])
        : []
    const consumed = new Set<number>()

    for (const [boxIndex, bounds] of (zone.drawing_bounds ?? []).entries()) {
      const baselineIndex = forceAll
        ? -1
        : baseline.findIndex(
            (candidate, index) =>
              !consumed.has(index) && sameRectangle(candidate, bounds)
          )
      if (baselineIndex >= 0) {
        consumed.add(baselineIndex)
        continue
      }

      if (!zone.zone_reference_image_id) {
        return `${zone.title} needs a reference image before its rectangle can be saved.`
      }
      const rectangle = pixelRectangleFromBounds(bounds)
      if (!isValidRectangle(rectangle)) {
        return `${zone.title} has an invalid rectangle ${boxIndex + 1}. Redraw it before saving.`
      }

      const size =
        measuredImage?.documentId === zone.zone_reference_image_id
          ? measuredImage
          : undefined
      if (size) {
        const measuredValidation = validateMeasuredPixelRectangle(
          rectangle,
          size
        )
        if (measuredValidation === 'minimum-size') {
          return `${zone.title} rectangle ${boxIndex + 1} must be at least 20 pixels wide and high.`
        }
        if (measuredValidation === 'outside-image') {
          return `${zone.title} rectangle ${boxIndex + 1} must stay inside its reference image.`
        }
        if (measuredValidation === 'invalid-image-size') {
          return `${zone.title} needs valid reference image dimensions before saving.`
        }
      } else if (!pixelRectangleMeetsMinimumSize(rectangle)) {
        return `${zone.title} rectangle ${boxIndex + 1} must be at least 20 pixels wide and high.`
      }

      const policy = buildZonePixelPolicy({
        documentId: zone.zone_reference_image_id,
        zones: proposedZones,
        measuredImage,
        excludeTarget: { zoneId: zone.id, boxIndex },
      })
      if ('error' in policy) return policy.error
      for (const obstacle of policy.obstacles) {
        if (rectanglesOverlap(rectangle, obstacle.rectangle)) {
          return `${zone.title} rectangle ${boxIndex + 1} overlaps ${obstacle.title}. Move or redraw the rectangle before saving.`
        }
      }
    }
  }
}
